import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { orderKeys } from "@/modules/orders/presentation/api/queries";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { paymentsApi } from "./client";

export const paymentKeys = {
  all: ["payments"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["payments", "list", q] as const,
  detail: (id: string) => ["payments", "detail", id] as const,
  summary: () => ["payments", "summary"] as const,
};

export function usePaymentsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: paymentKeys.list(q),
    queryFn: () => paymentsApi.list(q),
  });
}

export function usePaymentQuery(id: string) {
  return useQuery({
    queryKey: paymentKeys.detail(id),
    queryFn: () => paymentsApi.get(id),
    enabled: !!id,
  });
}

export function usePaymentSummaryQuery() {
  return useQuery({
    queryKey: paymentKeys.summary(),
    queryFn: () => paymentsApi.getSummary(),
  });
}

export function useRecordOrderPaymentRefund(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      paymentId: string;
      amount: number;
      note?: string | null;
    }) =>
      paymentsApi.recordRefund(args.paymentId, {
        amount: args.amount,
        note: args.note,
      }),
    onSuccess: (data, args) => {
      qc.setQueryData(paymentKeys.detail(args.paymentId), data);
      qc.invalidateQueries({ queryKey: paymentKeys.all });
      qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
      qc.invalidateQueries({ queryKey: orderKeys.all });
      qc.invalidateQueries({ queryKey: ["reports"] });
      toast.success("ບັນທຶກການຄືນເງິນສຳເລັດ");
    },
    onError: (error: unknown) => {
      toast.error("ບັນທຶກການຄືນເງິນລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}
